import {
  LogoutOutlined,
  KeyOutlined,
  LockOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Spin, message, Modal, Form, Input, Alert, Progress, Typography, Space } from 'antd';
import { createStyles } from 'antd-style';
import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { clearAuthStorage, getAccessToken } from '@/lib/authStorage';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  return <span className="anticon">{currentUser?.name}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    try {
      const token = getAccessToken();
      if (token) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      // ignore
    }

    // 清除本地token和用户信息
    clearAuthStorage();

    // 清除用户状态
    flushSync(() => {
      setInitialState((s) => ({ ...s, currentUser: undefined }));
    });

    message.success('退出登录成功');

    setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  };

  /**
   * 计算密码强度
   */
  const calculatePasswordStrength = (password: string) => {
    if (!password) {
      setPasswordStrength(0);
      return;
    }

    let strength = 0;

    if (password.length >= 6) strength += 20;
    if (password.length >= 8) strength += 20;
    if (password.length >= 12) strength += 20;
    if (/\d/.test(password)) strength += 20;
    if (/[a-zA-Z]/.test(password)) strength += 10;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 10;
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) strength += 10;

    setPasswordStrength(Math.min(strength, 100));
  };

  /**
   * 获取密码强度颜色和文字
   */
  const getPasswordStrengthStatus = () => {
    if (passwordStrength < 40) return { color: '#ff4d4f', text: '弱' };
    if (passwordStrength < 70) return { color: '#faad14', text: '中' };
    return { color: '#52c41a', text: '强' };
  };

  /**
   * 修改密码
   */
  const handleChangePassword = async (values: any) => {
    setLoading(true);
    try {
      const token = getAccessToken();
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
        }),
      });

      const data = await res.json();
      if (data.code !== 0) {
        throw new Error(data.msg || '密码修改失败');
      }

      message.success('成功');
      setChangePasswordVisible(false);
      form.resetFields();
      setPasswordStrength(0);

      // 清除本地token和用户信息
      clearAuthStorage();

      // 清除用户状态
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });

      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);
    } catch (error: any) {
      message.error(error.message || '密码修改失败');
    } finally {
      setLoading(false);
    }
  };

  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      loginOut();
      return;
    }
    if (key === 'changePassword') {
      setChangePasswordVisible(true);
      return;
    }
  };

  const loadingSpinner = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loadingSpinner;
  }

  const { currentUser } = initialState;

  if (!currentUser || !currentUser.name) {
    return loadingSpinner;
  }

  const menuItems = [
    {
      key: 'changePassword',
      icon: <KeyOutlined />,
      label: '修改密码',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  return (
    <>
      <HeaderDropdown
        menu={{
          selectedKeys: [],
          onClick: onMenuClick,
          items: menuItems,
        }}
      >
        {children}
      </HeaderDropdown>

      <Modal
        title="修改密码"
        open={changePasswordVisible}
        onCancel={() => {
          setChangePasswordVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={loading}
        destroyOnHidden
      >
        <Form
          form={form}
          labelCol={{ span: 5 }}
          wrapperCol={{ span: 19 }}
          onFinish={handleChangePassword}
        >
          <Form.Item
            name="oldPassword"
            label="原密码"
            rules={[{ required: true }]}
          >
            <Input placeholder="请输入原始密码" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[{ required: true }]}
          >
            <Input placeholder="请输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
